import { Notification } from "@/api/openapi";
import { useNotificationListElement } from "./notificationListElement/useNotificationListElement";
import { showTranslated } from "@/config/translator";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faCircleCheck,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";
import { formatDateFromLang } from "@/config/utils";
import { Tooltip } from "@fluentui/react-components";
import { clsx } from "clsx/lite";

type NotificationListElementProps = {
  notification: Notification;
} & (
  | {
      type: "mobile";
      removeNotification?: never;
      closeNotificationDrawer?: never;
      setNotificationRead?: never;
      updateHasNewNotifications?: never;
    }
  | {
      type: "desktop";
      removeNotification: (notificationId: string) => void;
      closeNotificationDrawer: () => void;
      setNotificationRead: (notificationId: string, isRead: boolean) => void;
      updateHasNewNotifications: () => void;
    }
);

export function NotificationListElement({
  notification,
  closeNotificationDrawer,
  setNotificationRead,
  updateHasNewNotifications,
  type,
  removeNotification,
}: NotificationListElementProps) {
  const { lang, isTablet, onClick, toggleRead } = useNotificationListElement(
    type === "mobile"
      ? { type, notification }
      : {
          type,
          closeNotificationDrawer,
          setNotificationRead,
          updateHasNewNotifications,
          notification,
        },
  );

  return (
    <Tooltip
      content={showTranslated(notification.body, lang)}
      relationship="description"
    >
      <button
        onClick={() => onClick(notification.id)}
        className={clsx(
          "flex",
          "flex-row",
          "justify-between",
          "items-center",
          "text-left",
          "cursor-pointer",
          isTablet ? "text-sm" : "text-base",
        )}
      >
        <div
          className={clsx(
            "flex",
            "flex-col",
            "w-full",
            "break-all",
            isTablet ? "gap-2" : "gap-0",
          )}
        >
          <div className="flex flex-row justify-between items-center">
            <div className="flex flex-row gap-1 items-center">
              {notification.readAt === null && (
                <div className="bg-primary size-2 rounded-full" />
              )}
              <div
                className={clsx(
                  notification.readAt !== null
                    ? "text-font-secondary"
                    : "text-primary",
                  isTablet ? "text-base" : "text-lg",
                  "font-bold",
                )}
              >
                {showTranslated(notification.subject, lang)}
              </div>
            </div>
            {isTablet && (
              <div className="text-font-secondary text-xs">
                {formatDateFromLang(new Date(notification.createdAt), lang)}
              </div>
            )}
          </div>

          <div className="flex flex-row justify-between gap-4">
            <div className="text-font-secondary line-clamp-1">
              {showTranslated(notification.body, lang)}
            </div>
            {isTablet && type === "desktop" && (
              <div className="flex flex-row gap-1">
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    removeNotification(notification.id);
                  }}
                  className="cursor-pointer event"
                >
                  <FontAwesomeIcon
                    icon={faTrash}
                    className={clsx("text-base", "text-red-500")}
                  />
                </button>
                <button onClick={toggleRead} className="cursor-pointer event">
                  <FontAwesomeIcon
                    icon={notification.readAt ? faCircleCheck : faCircle}
                    className={clsx(
                      notification.readAt
                        ? "text-green-500"
                        : "text-font-secondary",
                      "text-base",
                    )}
                  />
                </button>
              </div>
            )}
          </div>
        </div>

        {!isTablet && (
          <div className="text-font-secondary break-keep">
            {formatDateFromLang(new Date(notification.createdAt), lang)}
          </div>
        )}
      </button>
    </Tooltip>
  );
}
